import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { toast } from "sonner";
import { Loader2, Star } from "lucide-react";

export default function FeedbackPage() {
  const [f, setF] = useState({ name: "", email: "", rating: 5, message: "" });

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/feedback", {
        name: f.name.trim() || undefined,
        email: f.email.trim() || undefined,
        rating: f.rating,
        message: f.message.trim(),
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Thanks for the feedback — kia ora!");
      setF({ name: "", email: "", rating: 5, message: "" });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't send feedback."),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!f.message.trim()) { toast.error("Please write a short note."); return; }
    mut.mutate();
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Feedback" description="Tell us how we can make Allsale Classified better." />
      <Breadcrumbs items={[{ label: "Feedback" }]} />
      <h1 className="mt-6 font-display text-4xl font-black text-slate-900">We're all ears</h1>
      <p className="mt-2 text-slate-500">Rate your experience and let us know what we can do better.</p>

      <form onSubmit={submit} data-testid="feedback-form" className="mt-8 rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-5">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2 block">How would you rate us?</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setF({ ...f, rating: n })}
                className="p-1"
                aria-label={`${n} star`}
                data-testid={`feedback-star-${n}`}
              >
                <Star className={`w-7 h-7 ${n <= f.rating ? "fill-[color:var(--brand-orange)] text-[color:var(--brand-orange)]" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Your name (optional)</span>
          <input data-testid="feedback-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Email (optional)</span>
          <input data-testid="feedback-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Message *</span>
          <textarea data-testid="feedback-message" required value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="input min-h-32" placeholder="Tell us what's working or what could be better…" />
        </label>

        <button
          type="submit"
          data-testid="feedback-submit"
          disabled={mut.isPending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
        >
          {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Send feedback
        </button>

        <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
        .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
        textarea.input{padding-top:.75rem;padding-bottom:.75rem;line-height:1.5;}`}</style>
      </form>
    </div>
  );
}
